import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowDownIcon,
  ArrowTopRightOnSquareIcon,
  ArrowUpIcon,
  FunnelIcon,
} from "@heroicons/react/24/outline";
import { useParams } from "react-router-dom";
import { Tooltip } from "react-tooltip";

import {
  PastRunsSidebar,
  ReportTitle,
  RunLoading,
  RunProgress,
  TOOL_EMD,
  ToolTwoColumn,
  availabilityLabel,
  downloadCsv,
  truncateText,
  useToolRun,
} from "./shared";

const EMD_TIP_ID = "emd-row-tip";
const EMD_VOLUME_TIP_ID = "emd-volume-tip";
const BRANDED_TIP =
  "Branded: query embeds a known brand or trademark. Ranking an EMD here may compete with the brand itself.";

const EMPTY_FILTERS = {
  minVolume: "",
  maxKd: "",
  maxPrice: "",
  intent: "",
  build: "",
};

const VOLUME_WINDOWS = [
  { id: "1Y", label: "1Y", months: 12 },
  { id: "5Y", label: "5Y", months: 60 },
  { id: "All", label: "All", months: null },
];

const INTENT_LABELS = {
  informational: "Info",
  navigational: "Nav",
  commercial: "Commercial",
  transactional: "Transact",
};

const sortMonthly = (monthly) =>
  [...(monthly || [])].sort((a, b) => {
    const ay = a.year || 0;
    const by = b.year || 0;
    if (ay !== by) return ay - by;
    return (a.month || 0) - (b.month || 0);
  });

/** Average search volume over a window of monthly_searches. */
const volumeForWindow = (row, windowId) => {
  const cfg = VOLUME_WINDOWS.find((w) => w.id === windowId) || VOLUME_WINDOWS[0];
  const rows = sortMonthly(row?.monthly_searches).filter(
    (r) => r.search_volume != null && !Number.isNaN(Number(r.search_volume)),
  );
  if (!rows.length) return row?.volume ?? null;
  const slice = cfg.months == null ? rows : rows.slice(-cfg.months);
  if (!slice.length) return row?.volume ?? null;
  const sum = slice.reduce((acc, r) => acc + Number(r.search_volume), 0);
  return Math.round(sum / slice.length);
};

const monthlyForWindow = (monthly, windowId) => {
  const cfg = VOLUME_WINDOWS.find((w) => w.id === windowId) || VOLUME_WINDOWS[0];
  const rows = sortMonthly(monthly);
  if (cfg.months == null) return rows;
  return rows.slice(-cfg.months);
};

const CSV_COLUMNS = [
  { label: "domain", value: (r) => r.domain },
  { label: "query", value: (r) => r.query },
  { label: "category", value: (r) => r.category },
  { label: "volume", value: (r) => r.display_volume },
  { label: "group_volume", value: (r) => r.group_volume },
  { label: "volume_share", value: (r) => r.volume_share },
  { label: "weak_emd", value: (r) => (r.weak_emd ? "yes" : "") },
  { label: "branded", value: (r) => (r.branded ? "yes" : "") },
  { label: "core_keyword", value: (r) => r.core_keyword },
  { label: "cpc", value: (r) => r.cpc },
  { label: "difficulty", value: (r) => r.difficulty },
  { label: "intent", value: (r) => r.intent },
  { label: "opportunity_score", value: (r) => r.opportunity_score },
  { label: "site_type", value: (r) => r.site_type },
  { label: "monetization", value: (r) => r.monetization },
  { label: "price", value: (r) => r.price },
  { label: "register_url", value: (r) => r.register_url },
];

const CATEGORIES = [
  "",
  "Travel",
  "Local Services",
  "Product Reviews",
  "Tools/Calculators",
  "Finance",
  "Health",
  "Home & Garden",
  "Pets",
  "Hobbies",
  "Tech",
];

const formatVolume = (n) => {
  if (n == null) return "—";
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}K`;
  return String(n);
};

const formatSharePct = (share) => {
  if (share == null || Number.isNaN(Number(share))) return null;
  const pct = Number(share) * 100;
  if (pct < 0.1) return "<0.1%";
  if (pct < 1) return `${pct.toFixed(1)}%`;
  return `${Math.round(pct)}%`;
};

/** Short G-flag tip (same ballpark length as branded). */
const groupFlagTitle = (row) => {
  const core = (row.core_keyword || "").trim();
  if (core && core.toLowerCase() !== (row.query || "").toLowerCase()) {
    return `Grouped: Ads shares volume across close variants, led by “${core}”.`;
  }
  return "Grouped: Ads shares volume across close variants of this phrase.";
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/** Rich layout for the ~volume hover. */
const groupVolumeHtml = (row) => {
  const exact = formatVolume(row.display_volume ?? row.volume);
  const group = row.group_volume != null ? formatVolume(row.group_volume) : "—";
  const share = formatSharePct(row.volume_share);
  const core = (row.core_keyword || "").trim();
  const query = (row.query || "").trim();
  const variants = [];
  if (query) variants.push(query);
  if (core && core.toLowerCase() !== query.toLowerCase()) variants.push(core);

  const rowHtml = (label, value) =>
    `<div style="display:flex;justify-content:space-between;gap:1.25rem;align-items:baseline">` +
    `<span style="opacity:.65">${label}</span>` +
    `<span style="font-variant-numeric:tabular-nums;font-weight:600">${value}</span>` +
    `</div>`;

  const variantHtml = variants.length
    ? `<div style="margin-top:0.5rem">` +
      `<div style="opacity:.65;margin-bottom:0.2rem">In this group</div>` +
      variants
        .map(
          (kw, i) =>
            `<div style="font-size:11px;line-height:1.35">${escapeHtml(kw)}` +
            (i === 0 && share ? ` <span style="opacity:.55">· ${escapeHtml(share)} of group</span>` : "") +
            (i > 0 ? ` <span style="opacity:.55">· core</span>` : "") +
            `</div>`,
        )
        .join("") +
      `</div>`
    : "";

  return (
    `<div style="text-align:left;min-width:220px;max-width:280px">` +
    `<div style="display:flex;flex-direction:column;gap:0.35rem">` +
    rowHtml("EMD volume", `~${escapeHtml(exact)}`) +
    rowHtml("Group volume", escapeHtml(group)) +
    `</div>` +
    variantHtml +
    `<div style="margin-top:0.65rem;padding-top:0.55rem;border-top:1px solid rgba(255,255,255,.18);opacity:.8;font-size:11px;line-height:1.4">` +
    `Google may still send some related word-order traffic to this domain. Treat EMD volume as the reliable exact-match demand.` +
    `</div>` +
    `</div>`
  );
};

const formatCpc = (n) => {
  if (n == null || n === "") return "—";
  const num = Number(n);
  if (Number.isNaN(num)) return "—";
  return `$${num.toFixed(2)}`;
};

const MONTH_LABELS = [
  "", "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** Tiny SVG sparkline from DataForSEO monthly_searches. */
const VolumeSparkline = ({ monthly }) => {
  const [hover, setHover] = useState(null);

  const points = useMemo(() => {
    return sortMonthly(monthly)
      .filter((r) => r.search_volume != null && !Number.isNaN(Number(r.search_volume)))
      .map((r) => ({
        volume: Number(r.search_volume),
        year: r.year,
        month: r.month,
      }));
  }, [monthly]);

  if (points.length < 2) {
    return <span className="inline-block h-6 w-14 opacity-20" />;
  }

  const w = 56;
  const h = 22;
  const values = points.map((p) => p.volume);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const plotted = points.map((p, i) => ({
    ...p,
    x: (i / (points.length - 1)) * w,
    y: h - ((p.volume - min) / span) * (h - 2) - 1,
  }));
  const coords = plotted.map((p) => `${p.x},${p.y}`).join(" ");
  const rising = plotted[plotted.length - 1].volume >= plotted[0].volume;
  const stroke = rising ? "#10b981" : "#f59e0b";

  return (
    <span className="relative inline-flex shrink-0">
      {hover && (
        <span className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-1 -translate-x-1/2 whitespace-nowrap rounded-md bg-theme-text px-1.5 py-0.5 text-[10px] font-medium text-theme-main shadow">
          {MONTH_LABELS[hover.month] || hover.month} {hover.year}: {formatVolume(hover.volume)}
        </span>
      )}
      <svg
        width={w}
        height={h}
        viewBox={`0 0 ${w} ${h}`}
        className="overflow-visible"
        onMouseLeave={() => setHover(null)}
      >
        <polyline
          fill="none"
          stroke={stroke}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          points={coords}
        />
        {plotted.map((p) => (
          <circle
            key={`${p.year}-${p.month}`}
            cx={p.x}
            cy={p.y}
            r={hover === p ? 2.5 : 2}
            fill={hover === p ? stroke : "transparent"}
            stroke="transparent"
            strokeWidth={6}
            className="cursor-default"
            onMouseEnter={() => setHover(p)}
          >
            <title>
              {MONTH_LABELS[p.month] || p.month} {p.year}: {formatVolume(p.volume)}
            </title>
          </circle>
        ))}
      </svg>
    </span>
  );
};

const KdBadge = ({ value }) => {
  if (value == null) {
    return (
      <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-theme-secondary text-[11px] text-theme-text opacity-40">
        —
      </span>
    );
  }
  const n = Number(value);
  let ring = "bg-emerald-500/15 text-emerald-600";
  if (n > 60) ring = "bg-red-500/15 text-red-600";
  else if (n > 30) ring = "bg-amber-500/15 text-amber-600";
  return (
    <span
      className={`inline-flex h-8 w-8 items-center justify-center rounded-full text-[11px] font-semibold ${ring}`}
      title={`Keyword difficulty ${n}`}
    >
      {Math.round(n)}
    </span>
  );
};

const SortHeader = ({ label, column, sortKey, sortDir, onSort, className = "" }) => {
  const active = sortKey === column;
  return (
    <th className={`px-3 py-2.5 font-medium ${className}`}>
      <button
        type="button"
        onClick={() => onSort(column)}
        className={`inline-flex items-center gap-1 text-[11px] uppercase tracking-wide transition ${
          active ? "text-theme-text opacity-90" : "text-theme-text opacity-45 hover:opacity-70"
        }`}
      >
        {label}
        {active ? (
          sortDir === "asc" ? (
            <ArrowUpIcon className="h-3 w-3" />
          ) : (
            <ArrowDownIcon className="h-3 w-3" />
          )
        ) : null}
      </button>
    </th>
  );
};

const applyEmdFilters = (rows, filters, volumeWindow) => {
  const minVolume = filters.minVolume === "" ? null : Number(filters.minVolume);
  const maxKd = filters.maxKd === "" ? null : Number(filters.maxKd);
  const maxPrice = filters.maxPrice === "" ? null : Number(filters.maxPrice);
  return rows.filter((row) => {
    const vol = volumeForWindow(row, volumeWindow);
    if (minVolume != null && !Number.isNaN(minVolume) && (vol == null || Number(vol) < minVolume)) {
      return false;
    }
    if (maxKd != null && !Number.isNaN(maxKd) && (row.difficulty == null || Number(row.difficulty) > maxKd)) {
      return false;
    }
    if (maxPrice != null && !Number.isNaN(maxPrice)) {
      if (row.price == null || Number(row.price) > maxPrice) return false;
    }
    if (filters.intent && (row.intent || "") !== filters.intent) {
      return false;
    }
    if (filters.build && (row.monetization || "") !== filters.build) {
      return false;
    }
    return true;
  });
};

const VolumeWindowToggle = ({ value, onChange }) => (
  <div className="inline-flex rounded-lg border border-theme-secondary p-0.5">
    {VOLUME_WINDOWS.map((w) => (
      <button
        key={w.id}
        type="button"
        onClick={() => onChange(w.id)}
        className={`rounded-md px-2 py-1 text-[11px] font-medium transition ${
          value === w.id
            ? "bg-theme-secondary text-theme-text"
            : "text-theme-text opacity-50 hover:opacity-80"
        }`}
      >
        {w.label}
      </button>
    ))}
  </div>
);

const EmdFilterPopover = ({ filters, intentOptions, buildOptions, onChange, onClear }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const activeCount = [
    filters.minVolume,
    filters.maxKd,
    filters.maxPrice,
    filters.intent,
    filters.build,
  ].filter((v) => v !== "" && v != null).length;

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 rounded-lg border border-theme-secondary px-3 py-1.5 text-xs font-medium transition ${
          open || activeCount > 0
            ? "border-theme-primary/40 bg-theme-secondary text-theme-text"
            : "text-theme-text opacity-70 hover:opacity-100"
        }`}
      >
        <FunnelIcon className="h-3.5 w-3.5" />
        Filter
        {activeCount > 0 ? <span className="opacity-70">({activeCount})</span> : null}
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-1.5 w-64 rounded-xl border border-theme-secondary bg-theme-main p-3 shadow-xl">
          <div className="space-y-3">
            <label className="block">
              <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-theme-text opacity-50">
                Min volume
              </span>
              <input
                type="number"
                value={filters.minVolume}
                onChange={(e) => onChange("minVolume", e.target.value)}
                placeholder="Any"
                className="w-full rounded-lg border border-theme-secondary bg-theme-main px-2.5 py-1.5 text-xs text-theme-text"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-theme-text opacity-50">
                Max KD
              </span>
              <input
                type="number"
                value={filters.maxKd}
                onChange={(e) => onChange("maxKd", e.target.value)}
                placeholder="Any"
                className="w-full rounded-lg border border-theme-secondary bg-theme-main px-2.5 py-1.5 text-xs text-theme-text"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-theme-text opacity-50">
                Max price
              </span>
              <input
                type="number"
                value={filters.maxPrice}
                onChange={(e) => onChange("maxPrice", e.target.value)}
                placeholder="Any"
                className="w-full rounded-lg border border-theme-secondary bg-theme-main px-2.5 py-1.5 text-xs text-theme-text"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-theme-text opacity-50">
                Intent
              </span>
              <select
                value={filters.intent}
                onChange={(e) => onChange("intent", e.target.value)}
                className="w-full rounded-lg border border-theme-secondary bg-theme-main px-2.5 py-1.5 text-xs text-theme-text"
              >
                <option value="">All</option>
                {intentOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {INTENT_LABELS[opt] || opt}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-theme-text opacity-50">
                Build
              </span>
              <select
                value={filters.build}
                onChange={(e) => onChange("build", e.target.value)}
                className="w-full rounded-lg border border-theme-secondary bg-theme-main px-2.5 py-1.5 text-xs text-theme-text"
              >
                <option value="">All</option>
                {buildOptions.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </label>
          </div>
          {activeCount > 0 && (
            <button
              type="button"
              onClick={onClear}
              className="mt-3 text-xs font-medium text-theme-primary"
            >
              Clear filters
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const EmdResultsTable = ({ opportunities, volumeWindow }) => {
  const [sortKey, setSortKey] = useState("opportunity_score");
  const [sortDir, setSortDir] = useState("desc");
  const showPrice = opportunities.some((d) => d.price != null);

  const onSort = (column) => {
    if (sortKey === column) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(column);
      setSortDir(column === "difficulty" || column === "domain" || column === "intent" ? "asc" : "desc");
    }
  };

  const rows = useMemo(() => {
    const list = opportunities.map((row) => ({
      ...row,
      display_volume: volumeForWindow(row, volumeWindow),
      display_monthly: monthlyForWindow(row.monthly_searches, volumeWindow),
    }));
    const dir = sortDir === "asc" ? 1 : -1;
    list.sort((a, b) => {
      const key = sortKey === "volume" ? "display_volume" : sortKey;
      const av = a[key];
      const bv = b[key];
      if (key === "domain" || key === "query" || key === "monetization" || key === "intent") {
        return dir * String(av || "").localeCompare(String(bv || ""));
      }
      const an = av == null ? (sortDir === "asc" ? Infinity : -Infinity) : Number(av);
      const bn = bv == null ? (sortDir === "asc" ? Infinity : -Infinity) : Number(bv);
      return dir * (an - bn);
    });
    return list;
  }, [opportunities, sortKey, sortDir, volumeWindow]);

  if (!rows.length) {
    return (
      <p className="text-sm text-theme-text opacity-60">
        No available EMDs with these filters. Loosen volume/difficulty, add seeds, or try another category.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-theme-secondary">
      <Tooltip
        id={EMD_TIP_ID}
        place="top"
        className="z-50 !max-w-xs !text-left !text-xs !leading-snug"
      />
      <Tooltip
        id={EMD_VOLUME_TIP_ID}
        place="top"
        className="z-50 !max-w-sm !bg-[#26211a] !text-[#e8e2d6] !text-left !text-xs !leading-snug !opacity-100"
      />
      <table className="w-max text-left text-sm">
        <thead className="border-b border-theme-secondary bg-theme-secondary/30">
          <tr>
            <SortHeader label="Score" column="opportunity_score" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
            <SortHeader label="Domain" column="domain" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
            <SortHeader label="Volume" column="volume" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
            <SortHeader label="CPC" column="cpc" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
            <SortHeader label="KD" column="difficulty" sortKey={sortKey} sortDir={sortDir} onSort={onSort} className="text-center" />
            <SortHeader label="Intent" column="intent" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
            <SortHeader label="Build" column="monetization" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
            {showPrice && (
              <SortHeader label="Price" column="price" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
            )}
            <th className="px-3 py-2.5" />
          </tr>
        </thead>
        <tbody className="divide-y divide-theme-secondary/50">
          {rows.map((row) => (
            <tr key={row.domain} className="hover:bg-black hover:bg-opacity-[0.03]">
              <td className="whitespace-nowrap px-3 py-3 align-middle">
                <span className="text-sm font-semibold tabular-nums text-theme-primary">
                  {row.opportunity_score ?? "—"}
                </span>
              </td>
              <td className="whitespace-nowrap px-3 py-3 align-middle">
                <div className="font-medium text-theme-text">{row.domain}</div>
                {row.query ? (
                  <div className="mt-0.5 flex items-center gap-1.5 text-xs text-theme-text opacity-50">
                    <span>{row.query}</span>
                    {row.branded ? (
                      <span
                        className="inline-flex h-4 w-4 shrink-0 cursor-help items-center justify-center rounded text-[10px] font-bold text-amber-700 bg-amber-500/15"
                        data-tooltip-id={EMD_TIP_ID}
                        data-tooltip-content={BRANDED_TIP}
                      >
                        B
                      </span>
                    ) : null}
                    {row.weak_emd ? (
                      <span
                        className="inline-flex h-4 w-4 shrink-0 cursor-help items-center justify-center rounded text-[10px] font-bold text-sky-700 bg-sky-500/15"
                        data-tooltip-id={EMD_TIP_ID}
                        data-tooltip-content={groupFlagTitle(row)}
                      >
                        G
                      </span>
                    ) : null}
                  </div>
                ) : null}
              </td>
              <td className="whitespace-nowrap px-3 py-3 align-middle">
                <div className="flex items-center gap-2.5">
                  {row.weak_emd ? (
                    <span
                      className="cursor-help font-medium tabular-nums text-theme-text underline decoration-dotted decoration-current/40 underline-offset-2"
                      data-tooltip-id={EMD_VOLUME_TIP_ID}
                      data-tooltip-html={groupVolumeHtml(row)}
                    >
                      ~{formatVolume(row.display_volume)}
                    </span>
                  ) : (
                    <span className="font-medium tabular-nums text-theme-text">
                      {formatVolume(row.display_volume)}
                    </span>
                  )}
                  <VolumeSparkline monthly={row.display_monthly} />
                </div>
              </td>
              <td className="whitespace-nowrap px-3 py-3 align-middle tabular-nums text-theme-text">
                {formatCpc(row.cpc)}
              </td>
              <td className="whitespace-nowrap px-3 py-3 text-center align-middle">
                <KdBadge value={row.difficulty} />
              </td>
              <td className="whitespace-nowrap px-3 py-3 align-middle">
                {row.intent ? (
                  <span
                    className="inline-flex whitespace-nowrap rounded-md bg-theme-secondary px-1.5 py-0.5 text-[11px] text-theme-text opacity-70"
                    title={row.intent}
                  >
                    {INTENT_LABELS[row.intent] || row.intent}
                  </span>
                ) : (
                  <span className="text-xs text-theme-text opacity-40">—</span>
                )}
              </td>
              <td className="whitespace-nowrap px-3 py-3 align-middle">
                {row.monetization ? (
                  <span className="inline-flex whitespace-nowrap rounded-md bg-theme-secondary px-1.5 py-0.5 text-[11px] text-theme-text opacity-70">
                    {row.monetization}
                  </span>
                ) : (
                  <span className="text-xs text-theme-text opacity-40">—</span>
                )}
              </td>
              {showPrice && (
                <td className="whitespace-nowrap px-3 py-3 align-middle tabular-nums text-theme-text opacity-80">
                  {row.price != null ? `$${row.price}` : "—"}
                  {row.premium ? (
                    <span className="ml-1 text-[10px] uppercase opacity-50">prem</span>
                  ) : null}
                </td>
              )}
              <td className="whitespace-nowrap px-3 py-3 text-right align-middle">
                <a
                  href={row.register_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 rounded-lg border border-theme-secondary px-2.5 py-1.5 text-xs font-medium text-theme-text hover:border-theme-primary hover:text-theme-primary"
                >
                  Register
                  <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 opacity-60" />
                </a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const EmdOpportunities = ({ currentUser }) => {
  const { runHash } = useParams();
  const {
    run,
    error,
    submitting,
    loading,
    start,
    retry,
    openRun,
    newRun,
    runsVersion,
    isActive,
  } = useToolRun(TOOL_EMD, runHash);
  const showBuildCost = Boolean(currentUser?.features?.build_cost);

  const [category, setCategory] = useState("");
  const [seedKeywords, setSeedKeywords] = useState("");
  const [minVolume, setMinVolume] = useState("50");
  const [maxDifficulty, setMaxDifficulty] = useState("60");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [volumeWindow, setVolumeWindow] = useState("1Y");

  const opportunities = run?.result?.opportunities || [];
  const showForm = !runHash;

  const intentOptions = useMemo(() => {
    const set = new Set();
    opportunities.forEach((row) => {
      if (row.intent) set.add(row.intent);
    });
    return [...set].sort();
  }, [opportunities]);

  const buildOptions = useMemo(() => {
    const set = new Set();
    opportunities.forEach((row) => {
      if (row.monetization) set.add(row.monetization);
    });
    return [...set].sort();
  }, [opportunities]);

  const filtered = useMemo(
    () => applyEmdFilters(opportunities, filters, volumeWindow),
    [opportunities, filters, volumeWindow],
  );

  const csvRows = useMemo(
    () =>
      filtered.map((row) => ({
        ...row,
        display_volume: volumeForWindow(row, volumeWindow),
      })),
    [filtered, volumeWindow],
  );

  const reportTitle = useMemo(() => {
    const cat = (run?.input?.category || "").trim();
    const seeds = (run?.input?.seed_keywords || "").trim();
    if (cat && seeds) return truncateText(`${cat} with ${seeds}`);
    if (cat) return cat;
    if (seeds) return truncateText(seeds);
    return "All categories";
  }, [run]);

  const countLabel =
    filtered.length < opportunities.length
      ? `${filtered.length}/${opportunities.length}`
      : String(opportunities.length);

  // Reset filters / volume window when switching runs.
  useEffect(() => {
    setFilters(EMPTY_FILTERS);
    setVolumeWindow("1Y");
  }, [runHash]);

  const onSubmit = (e) => {
    e.preventDefault();
    start({
      category,
      seed_keywords: seedKeywords,
      min_volume:
        minVolume === "" || Number.isNaN(Number(minVolume))
          ? 50
          : Number(minVolume),
      max_difficulty:
        maxDifficulty === "" || Number.isNaN(Number(maxDifficulty))
          ? 60
          : Number(maxDifficulty),
    });
  };

  const onFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const main = (
    <div className="space-y-4">
      {error && <p className="text-sm text-red-600">{error}</p>}

      {showForm && (
        <>
          <p className="text-sm text-theme-text opacity-60">
            Find search queries whose exact-match .com is still available.
          </p>
          <form onSubmit={onSubmit} className="space-y-4 rounded-xl border border-theme-secondary p-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm">
              <span className="font-medium text-theme-text">Category (optional)</span>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="mt-1 w-full rounded-lg border border-theme-secondary bg-theme-main px-3 py-2 text-theme-text"
              >
                <option value="">All categories</option>
                {CATEGORIES.filter(Boolean).map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              <span className="font-medium text-theme-text">Seed keywords (optional)</span>
              <input
                value={seedKeywords}
                onChange={(e) => setSeedKeywords(e.target.value)}
                placeholder="pool leak, robot mower"
                className="mt-1 w-full rounded-lg border border-theme-secondary bg-theme-main px-3 py-2 text-theme-text"
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium text-theme-text">Min monthly volume</span>
              <input
                type="number"
                value={minVolume}
                onChange={(e) => setMinVolume(e.target.value)}
                className="mt-1 w-full rounded-lg border border-theme-secondary bg-theme-main px-3 py-2 text-theme-text"
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium text-theme-text">Max keyword difficulty</span>
              <input
                type="number"
                value={maxDifficulty}
                onChange={(e) => setMaxDifficulty(e.target.value)}
                className="mt-1 w-full rounded-lg border border-theme-secondary bg-theme-main px-3 py-2 text-theme-text"
              />
            </label>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="rounded-lg bg-theme-primary px-4 py-2 text-sm font-medium text-theme-button disabled:opacity-40"
          >
            {submitting ? "Starting…" : "Find EMDs"}
          </button>
          </form>
        </>
      )}

      {runHash && loading && <RunLoading />}

      {runHash && !loading && (isActive || run?.status === "failed") && (
        <RunProgress
          run={run}
          showBuildCost={showBuildCost}
          onRetry={retry}
          retrying={submitting}
        />
      )}

      {run?.status === "succeeded" && (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <ReportTitle title={reportTitle} countLabel={countLabel} />
              {availabilityLabel(run?.result?.summary) && (
                <p className="mt-1 text-xs text-theme-text opacity-50">
                  {availabilityLabel(run.result.summary)}
                </p>
              )}
            </div>
            {opportunities.length > 0 && (
              <div className="flex shrink-0 items-center gap-2">
                <VolumeWindowToggle value={volumeWindow} onChange={setVolumeWindow} />
                <EmdFilterPopover
                  filters={filters}
                  intentOptions={intentOptions}
                  buildOptions={buildOptions}
                  onChange={onFilterChange}
                  onClear={() => setFilters(EMPTY_FILTERS)}
                />
                <button
                  type="button"
                  onClick={() => downloadCsv("emd-opportunities.csv", csvRows, CSV_COLUMNS)}
                  className="rounded-lg border border-theme-secondary px-3 py-1.5 text-xs font-medium text-theme-text opacity-70 hover:opacity-100"
                >
                  Export CSV
                </button>
              </div>
            )}
          </div>
          <EmdResultsTable opportunities={filtered} volumeWindow={volumeWindow} />
          <RunProgress run={run} showBuildCost={showBuildCost} />
        </div>
      )}
    </div>
  );

  return (
    <ToolTwoColumn
      main={main}
      side={(
        <PastRunsSidebar
          toolId={TOOL_EMD}
          activeHash={runHash}
          onOpen={openRun}
          onNew={newRun}
          refreshKey={runsVersion}
        />
      )}
    />
  );
};

export default EmdOpportunities;
